/**
 * Debug Logger — conditional console output
 *
 * In production builds, debug/info/warn messages are suppressed.
 * Errors always log. Set `localStorage.debug = '*'` to enable all,
 * or `localStorage.debug = 'BufferSync,Workspace'` for specific tags.
 *
 * @module @humanizer/studio/utils/debug
 */

const isDev = import.meta.env?.DEV ?? false;

/** Check if a tag is enabled (DEV mode, or localStorage.debug pattern) */
function isEnabled(tag: string): boolean {
  if (isDev) return true;
  try {
    const pattern = localStorage.getItem('debug');
    if (!pattern) return false;
    if (pattern === '*') return true;
    return pattern.split(',').some(p => p.trim() === tag);
  } catch {
    return false;
  }
}

/** Create a scoped logger. Debug/info/warn suppressed in production unless opted-in. */
export function createLogger(tag: string) {
  const prefix = `[${tag}]`;
  return {
    debug: (...args: unknown[]) => { if (isEnabled(tag)) console.debug(prefix, ...args); },
    info: (...args: unknown[]) => { if (isEnabled(tag)) console.info(prefix, ...args); },
    log: (...args: unknown[]) => { if (isEnabled(tag)) console.log(prefix, ...args); },
    warn: (...args: unknown[]) => { if (isEnabled(tag)) console.warn(prefix, ...args); },
    error: (...args: unknown[]) => { console.error(prefix, ...args); },
  };
}
